import React from "react";
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { PayslipData } from "../../types/payslip";
import { formatINR, numberToIndianWords } from "../../utils/numberToWords";

interface PayslipDocumentProps {
  data: PayslipData;
  id?: string;
}

export const PayslipDocument: React.FC<PayslipDocumentProps> = ({ data, id = "printable-payslip" }) => {
  const {
    company,
    employee,
    earnings,
    deductions,
    payMonth,
    payYear,
    notes,
  } = data;

  const totalGrossEarnings = earnings.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const totalDeductions = deductions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const netPay = Math.max(0, totalGrossEarnings - totalDeductions);
  const netPayInWords = numberToIndianWords(netPay);

  // Pad the rows so earnings and deductions tables are visually aligned
  const maxRows = Math.max(earnings.length, deductions.length, 4);
  const paddedEarnings = [...earnings];
  const paddedDeductions = [...deductions];
  while (paddedEarnings.length < maxRows) {
    paddedEarnings.push({ id: `pad-e-${paddedEarnings.length}`, name: "", amount: 0 });
  }
  while (paddedDeductions.length < maxRows) {
    paddedDeductions.push({ id: `pad-d-${paddedDeductions.length}`, name: "", amount: 0 });
  }

  // Address formatting
  const companyAddressDisplay = [
    company.addressLine1,
    company.addressLine2,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Box
      id={id}
      sx={{
        width: "100%",
        maxWidth: "800px",
        mx: "auto",
        backgroundColor: "#ffffff",
        color: "#1e293b",
        p: { xs: 2.5, sm: 4 },
        borderRadius: 2,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        border: "1px solid #e2e8f0",
        fontFamily: "'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        "@media print": {
          boxShadow: "none",
          border: "1px solid #e2e8f0",
          p: 2.5,
          m: "0 auto",
          maxWidth: "100%",
          width: "100%",
        },
      }}
    >
      {/* 1. Header with Company Details and Logo */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "2px solid #f1f5f9",
          pb: 2.5,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {company.logoUrl ? (
            <Box
              component="img"
              src={company.logoUrl}
              alt={company.companyName}
              sx={{
                maxHeight: 65,
                maxWidth: 160,
                objectFit: "contain",
              }}
            />
          ) : (
            <Box
              sx={{
                height: 50,
                px: 2,
                borderRadius: 1.5,
                bgcolor: "#fff7ed",
                border: "1px solid #fed7aa",
                display: "flex",
                alignItems: "center",
                color: "#ea580c",
                fontWeight: 800,
                fontSize: "1.25rem",
              }}
            >
              {company.companyName || "KINNOW"}
            </Box>
          )}
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: "#0f172a", lineHeight: 1.2 }}>
              {company.companyName}
            </Typography>
            <Typography sx={{ fontSize: "0.775rem", color: "#64748b", mt: 0.5, lineHeight: 1.4, maxWidth: "520px" }}>
              {companyAddressDisplay}
            </Typography>
            {(company.gstin || company.pan) && (
              <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8", mt: 0.25 }}>
                {[company.gstin ? `GSTIN: ${company.gstin}` : "", company.pan ? `PAN: ${company.pan}` : ""]
                  .filter(Boolean)
                  .join(" | ")}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Top Right: Only Month and Year */}
        <Box sx={{ textAlign: "right", minWidth: "140px" }}>
          <Typography
            sx={{
              fontSize: "1.15rem",
              fontWeight: 800,
              color: "#f97316",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Payslip
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#334155", mt: 0.25 }}>
            {payMonth} {payYear}
          </Typography>
        </Box>
      </Box>

      {/* 2. Employee Details Grid */}
      <Box
        sx={{
          my: 2.5,
          p: 2,
          bgcolor: "#f8fafc",
          borderRadius: 1.5,
          border: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          {/* Left Column */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <DetailRow label="Employee Name" value={employee.name} boldValue />
            <DetailRow label="Employee ID" value={employee.employeeId} />
            <DetailRow label="Designation" value={employee.designation} />
            <DetailRow label="Department" value={employee.department} />
            <DetailRow label="Total Working Days" value={employee.totalWorkingDays?.toString() || "30"} />
            <DetailRow label="Paid Days / Worked" value={employee.paidDays?.toString() || "30"} />
            {employee.lopDays > 0 && <DetailRow label="LOP (Loss of Pay)" value={employee.lopDays?.toString()} />}
          </Box>

          {/* Right Column */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {employee.bankName && <DetailRow label="Bank Name" value={employee.bankName} />}
            {employee.accountNumber && <DetailRow label="Bank A/C No" value={employee.accountNumber} />}
            {employee.ifscCode && <DetailRow label="IFSC Code" value={employee.ifscCode} />}
            {employee.panNumber && <DetailRow label="PAN Number" value={employee.panNumber} />}
            {employee.uanNumber && <DetailRow label="UAN Number" value={employee.uanNumber} />}
            {employee.pfAccountNumber && <DetailRow label="PF A/C Number" value={employee.pfAccountNumber} />}
            {employee.esiNumber && <DetailRow label="ESI Number" value={employee.esiNumber} />}

            {/* Custom fields */}
            {employee.customFields?.map(
              (cf) => cf.label && cf.value && <DetailRow key={cf.id} label={cf.label} value={cf.value} />
            )}
          </Box>
        </Box>
      </Box>

      {/* 3. Salary Breakdown Table (Earnings vs Deductions) */}
      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: 1.5,
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #e2e8f0" }}>
          {/* Earnings Header */}
          <Box sx={{ bgcolor: "#f1f5f9", p: 1.25, borderRight: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#334155", textTransform: "uppercase" }}>
              Earnings
            </Typography>
          </Box>
          {/* Deductions Header */}
          <Box sx={{ bgcolor: "#f1f5f9", p: 1.25 }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#334155", textTransform: "uppercase" }}>
              Deductions
            </Typography>
          </Box>
        </Box>

        {/* Rows */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
          {/* Earnings Column */}
          <Box sx={{ borderRight: "1px solid #e2e8f0" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600, py: 0.75, color: "#64748b" }}>Component</TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: 600, py: 0.75, color: "#64748b" }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paddedEarnings.map((item, idx) => (
                  <TableRow key={item.id || idx} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ fontSize: "0.8rem", py: 0.75, color: item.name ? "#1e293b" : "transparent" }}>
                      {item.name || "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.8rem", py: 0.75, color: item.name ? "#1e293b" : "transparent" }}>
                      {item.name ? formatINR(item.amount) : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Deductions Column */}
          <Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600, py: 0.75, color: "#64748b" }}>Component</TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: 600, py: 0.75, color: "#64748b" }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paddedDeductions.map((item, idx) => (
                  <TableRow key={item.id || idx} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ fontSize: "0.8rem", py: 0.75, color: item.name ? "#1e293b" : "transparent" }}>
                      {item.name || "-"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.8rem", py: 0.75, color: item.name ? "#1e293b" : "transparent" }}>
                      {item.name ? formatINR(item.amount) : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Totals Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            borderTop: "2px solid #e2e8f0",
            bgcolor: "#f8fafc",
          }}
        >
          <Box sx={{ p: 1.25, display: "flex", justifyContent: "space-between", borderRight: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: "0.825rem", fontWeight: 700, color: "#0f172a" }}>Total Earnings (Gross)</Typography>
            <Typography sx={{ fontSize: "0.825rem", fontWeight: 700, color: "#0f172a" }}>
              {formatINR(totalGrossEarnings)}
            </Typography>
          </Box>
          <Box sx={{ p: 1.25, display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.825rem", fontWeight: 700, color: "#0f172a" }}>Total Deductions</Typography>
            <Typography sx={{ fontSize: "0.825rem", fontWeight: 700, color: "#0f172a" }}>
              {formatINR(totalDeductions)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* 4. Net Salary & Amount in Words Banner */}
      <Box
        sx={{
          p: 2,
          borderRadius: 1.5,
          bgcolor: "#f0fdf4",
          border: "1.5px solid #bbf7d0",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#166534", textTransform: "uppercase" }}>
            Total Net Payable
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#14532d", mt: 0.25 }}>
            Amount in Words: <strong>{netPayInWords}</strong>
          </Typography>
        </Box>
        <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
          <Typography
            sx={{
              fontSize: "1.45rem",
              fontWeight: 800,
              color: "#15803d",
              letterSpacing: "-0.02em",
            }}
          >
            {formatINR(netPay)}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "#166534" }}>Gross Pay - Total Deductions</Typography>
        </Box>
      </Box>

      {/* 5. Notes & System Generated Notice (No physical signature needed) */}
      <Box
        sx={{
          pt: 2,
          borderTop: "1px dashed #cbd5e1",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {notes && (
          <Box sx={{ width: "100%", textAlign: "left" }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#475569", textTransform: "uppercase" }}>
              Notes / Remarks
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#64748b", whiteSpace: "pre-line", mt: 0.25 }}>
              {notes}
            </Typography>
          </Box>
        )}
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: "#64748b",
            fontWeight: 600,
            letterSpacing: "0.03em",
            textAlign: "center",
            mt: 0.5,
          }}
        >
          -- This is a system-generated document. --
        </Typography>
      </Box>
    </Box>
  );
};

interface DetailRowProps {
  label: string;
  value: string;
  boldValue?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, boldValue = false }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem" }}>
    <Typography sx={{ fontSize: "0.8rem", color: "#64748b", minWidth: 130 }}>{label}:</Typography>
    <Typography
      sx={{
        fontSize: "0.8rem",
        color: boldValue ? "#0f172a" : "#334155",
        fontWeight: boldValue ? 700 : 500,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value || "-"}
    </Typography>
  </Box>
);

export default PayslipDocument;
